import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import api from "@/lib/api";
import useIdleLogout from "@/hooks/useIdleLogout";

const AuthContext = createContext(null);

/**
 * Auth flow:
 * 1. Try POST /api/v2/auth/login (RBAC + super_admin support). Returns {token, user:{role,...}}.
 * 2. If v2 fails with 401 (bad creds) surface the error.
 * 3. If v2 fails with a network/404/500 error, fall back to legacy POST /api/auth/login
 *    so existing admin accounts keep working.
 *
 * Token is stored under `adelina_token`; `user.role` is one of:
 * super_admin | admin | editor | staff — CMS tabs check the role
 * to gate super-admin-only actions (skills, biodata schema, translations, notifications).
 */
export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(() => localStorage.getItem("adelina_token"));
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const stored = localStorage.getItem("adelina_token");
    if (!stored) { setLoading(false); return; }
    // Try v2 /me first (more info), fall back to legacy.
    api.get("/v2/auth/me")
      .then((r) => setUser(r.data))
      .catch(() =>
        api.get("/auth/me")
          .then((r) => setUser({ ...r.data, role: r.data?.role || "admin" }))
          .catch(() => { localStorage.removeItem("adelina_token"); setToken(null); })
      )
      .finally(() => setLoading(false));
  }, []);

  const login = async (email, password) => {
    // Try v2 first (super_admin & RBAC users), fall back to legacy on ANY v2 failure.
    // v2 returns 401 both for unknown users AND wrong passwords, so we cannot distinguish
    // "not-a-v2-user" from "bad password" — must always attempt legacy fallback.
    try {
      const { data } = await api.post("/v2/auth/login", { email, password });
      localStorage.setItem("adelina_token", data.token);
      setToken(data.token);
      setUser(data.user || { email, role: "admin" });
      return { ...data, requires_password_change: data.user?.must_change_password };
    } catch (v2err) {
      try {
        const { data } = await api.post("/auth/login", { email, password });
        localStorage.setItem("adelina_token", data.token);
        setToken(data.token);
        setUser({ email: data.email, role: data.role || "admin" });
        return data;
      } catch (legacyErr) {
        // Both failed — surface the most specific error (prefer legacy's 401 message)
        throw legacyErr?.response?.status === 401 ? legacyErr : v2err;
      }
    }
  };

  const logout = useCallback(() => {
    // Fire-and-forget backend logout so the httpOnly cookies get cleared
    // server-side. If the network call fails we still clear local state.
    Promise.allSettled([
      api.post("/v2/auth/logout").catch(() => null),
      api.post("/auth/logout").catch(() => null),
    ]).finally(() => {});
    localStorage.removeItem("adelina_token");
    setToken(null);
    setUser(null);
  }, []);

  // Auto-logout after 5 minutes of inactivity (per stakeholder request).
  // Only active when a session is present, otherwise the timer is a no-op.
  useIdleLogout({
    enabled: !!token,
    idleMinutes: 5,
    onLogout: logout,
    storageKey: "adelina_admin_last_activity",
  });

  // Memoize the context value so consumers only re-render when auth state
  // actually changes (not on every parent re-render).
  const value = useMemo(
    () => ({ user, token, loading, login, logout }),
    [user, token, loading, logout]
  );

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => useContext(AuthContext);
